/**
 * AddTextItemDialog - Dialog para adicionar/editar item de texto
 * 
 * Permite inserir mensagem de texto com suporte a variáveis
 * como {{nome}}, {{variavel1}} para personalização
 * 
 * FASE 2: Adicionado seletor de variáveis disponíveis da lista
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ListVariableSelector } from './ListVariableSelector';

// =====================================================
// INTERFACES
// =====================================================

interface AddTextItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (textContent: string) => void;
  initialValue?: string;
  /** Variáveis disponíveis da lista de campanha (ex: ['variavel1', 'variavel2']) */
  availableVariables?: string[];
}

// =====================================================
// COMPONENTE
// =====================================================

export function AddTextItemDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
  availableVariables = [],
}: AddTextItemDialogProps) {
  const [textContent, setTextContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      setTextContent(initialValue || '');
    }
  }, [open, initialValue]);

  /**
   * Handler para inserir variável no cursor
   */
  const handleInsertVariable = (variable: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      // Fallback: adicionar no final
      setTextContent(prev => prev + variable);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = textContent.substring(0, start);
    const after = textContent.substring(end);
    
    const newContent = before + variable + after;
    setTextContent(newContent);
    
    // Reposicionar cursor após a variável inserida
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Handler de salvar
  const handleSave = () => {
    if (textContent.trim()) {
      onSave(textContent.trim());
    }
  };

  // Validação
  const isValid = textContent.trim().length > 0;

  /**
   * Gera preview substituindo variáveis com valores de exemplo
   */
  const getPreviewText = () => {
    let preview = textContent;
    
    // Substituir variáveis padrão
    preview = preview.replace(/\{\{nome\}\}/gi, 'João Silva');
    preview = preview.replace(/\{\{telefone\}\}/gi, '5562999999999');
    preview = preview.replace(/\{\{email\}\}/gi, 'joao@exemplo.com');
    
    // Substituir variáveis customizadas
    availableVariables.forEach((varName, index) => {
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'gi');
      preview = preview.replace(regex, `Valor ${index + 1}`);
    });
    
    return preview;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {initialValue ? 'Editar Mensagem de Texto' : 'Adicionar Mensagem de Texto'}
          </DialogTitle>
          <DialogDescription>
            Digite a mensagem que será enviada. Use variáveis para personalização.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Seletor de variáveis */}
          <ListVariableSelector
            variableColumns={availableVariables}
            onInsert={handleInsertVariable}
          />

          <div className="space-y-2">
            <Label htmlFor="text-content">Mensagem *</Label>
            <Textarea
              ref={textareaRef}
              id="text-content"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Digite sua mensagem aqui..."
              rows={6}
              className="resize-none"
            />
          </div>

          {/* Pré-visualização */}
          {textContent && (
            <div className="space-y-2">
              <Label>Pré-visualização</Label>
              <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-[150px] overflow-y-auto">
                {getPreviewText()}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {initialValue ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
